import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Label,
  Input,
  FormText,
  Spinner,
  Row,
  Col,
  Alert,
} from "reactstrap";
import { FaPlus, FaTrash, FaArrowUp, FaArrowDown, FaCog, FaListUl, FaExclamationTriangle } from "react-icons/fa";
import { createSurvey, updateSurvey } from "../../../helpers/surveyApi";
import { successMessage, dangerMessage } from "../../Toast/Toast";

const SurveyForm = ({ eventId, survey, token, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    isActive: true,
    settings: {
      allowAnonymous: false,
      requireEmail: true,
      allowMultipleSubmissions: false,
    },
    questions: [],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [settingsError, setSettingsError] = useState(null);

  useEffect(() => {
    if (survey) {
      setFormData({
        title: survey.title || "",
        description: survey.description || "",
        isActive: survey.isActive !== undefined ? survey.isActive : true,
        settings: {
          allowAnonymous: survey.settings?.allowAnonymous || false,
          requireEmail:
            survey.settings?.requireEmail !== undefined
              ? survey.settings.requireEmail
              : true,
          allowMultipleSubmissions:
            survey.settings?.allowMultipleSubmissions || false,
        },
        questions: survey.questions || [],
      });
    }
  }, [survey]);

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;

    if (name === "title") {
      if (value.length > 100) {
        dangerMessage("Survey title cannot be longer than 100 characters.");
      }
      const trimmedValue = value.slice(0, 100);
      setFormData((prev) => ({
        ...prev,
        title: trimmedValue,
      }));
      return;
    }

    if (name === "description") {
      if (value.length > 200) {
        dangerMessage("Survey description cannot be longer than 200 characters.");
      }
      const trimmedValue = value.slice(0, 200);
      setFormData((prev) => ({
        ...prev,
        description: trimmedValue,
      }));
      return;
    }
    
    if (name.startsWith("settings.")) {
      const settingKey = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        settings: {
          ...prev.settings,
          [settingKey]: type === "checkbox" ? checked : value,
        },
      }));
    } else if (name === "isActive") {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // ✅ FIX: Handle toggle changes with mutual exclusivity
  const handleToggleChange = (name, isChecked) => {
    // Clear settings error when user changes a toggle
    if (settingsError) {
      setSettingsError(null);
    }

    if (name.startsWith("settings.")) {
      const settingKey = name.split(".")[1];
      
      // ✅ MUTUAL EXCLUSIVITY: allowAnonymous and requireEmail cannot both be true
      if (settingKey === "allowAnonymous" && isChecked) {
        // If enabling anonymous, disable requireEmail
        setFormData((prev) => ({
          ...prev,
          settings: {
            ...prev.settings,
            allowAnonymous: true,
            requireEmail: false, // Force disable requireEmail
          },
        }));
      } else if (settingKey === "requireEmail" && isChecked) {
        // If enabling requireEmail, disable allowAnonymous
        setFormData((prev) => ({
          ...prev,
          settings: {
            ...prev.settings,
            requireEmail: true,
            allowAnonymous: false, // Force disable allowAnonymous
          },
        }));
      } else {
        // Normal toggle behavior for other settings
        setFormData((prev) => ({
          ...prev,
          settings: {
            ...prev.settings,
            [settingKey]: isChecked,
          },
        }));
      }
    } else if (name === "isActive") {
      setFormData((prev) => ({
        ...prev,
        [name]: isChecked,
      }));
    } 
    // Handle question required toggles
    else if (name.startsWith("question_") && name.endsWith("_required")) {
      const parts = name.split("_");
      const index = parseInt(parts[1], 10);
      
      if (!isNaN(index) && index >= 0) {
        setFormData((prev) => {
          const newQuestions = [...prev.questions];
          newQuestions[index] = {
            ...newQuestions[index],
            required: isChecked,
          };
          return {
            ...prev,
            questions: newQuestions,
          };
        });
      }
    }
    else {
      setFormData((prev) => ({
        ...prev,
        [name]: isChecked,
      }));
    }
  };

  const addQuestion = () => {
    const newQuestion = {
      type: "text",
      question: "",
      required: false,
      ratingScale: 5,
      options: [],
    };
    setFormData((prev) => ({
      ...prev,
      questions: [...prev.questions, newQuestion],
    }));
  };

  const removeQuestion = (index) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index),
    }));
  };

  const moveQuestion = (index, direction) => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === formData.questions.length - 1)
    ) {
      return;
    }

    const newQuestions = [...formData.questions];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    [newQuestions[index], newQuestions[targetIndex]] = [
      newQuestions[targetIndex],
      newQuestions[index],
    ];
    setFormData((prev) => ({
      ...prev,
      questions: newQuestions,
    }));
  };

  const updateQuestion = (index, field, value) => {
    setFormData((prev) => {
      const newQuestions = [...prev.questions];
      newQuestions[index] = {
        ...newQuestions[index],
        [field]: value,
      };
      return {
        ...prev,
        questions: newQuestions,
      };
    });
  };

  const addOption = (questionIndex) => {
    setFormData((prev) => {
      const newQuestions = [...prev.questions];
      if (!newQuestions[questionIndex].options) {
        newQuestions[questionIndex].options = [];
      }
      newQuestions[questionIndex].options = [
        ...newQuestions[questionIndex].options,
        "",
      ];
      return {
        ...prev,
        questions: newQuestions,
      };
    });
  };

  const removeOption = (questionIndex, optionIndex) => {
    setFormData((prev) => {
      const newQuestions = [...prev.questions];
      newQuestions[questionIndex].options = newQuestions[
        questionIndex
      ].options.filter((_, i) => i !== optionIndex);
      return {
        ...prev,
        questions: newQuestions,
      };
    });
  };

  const updateOption = (questionIndex, optionIndex, value) => {
    setFormData((prev) => {
      const newQuestions = [...prev.questions];
      newQuestions[questionIndex].options[optionIndex] = value;
      return {
        ...prev,
        questions: newQuestions,
      };
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    // ✅ Validate mutually exclusive settings
    if (formData.settings.allowAnonymous && formData.settings.requireEmail) {
      setSettingsError("Cannot allow anonymous submissions and require email at the same time. Please choose one option.");
      return false;
    }
    setSettingsError(null);

    formData.questions.forEach((question, index) => {
      if (!question.question.trim()) {
        newErrors[`question_${index}`] = "Question text is required";
      }

      if (question.type === "rating" && question.ratingScale < 2) {
        newErrors[`rating_${index}`] = "Rating scale must be at least 2";
      }

      if (question.type === "multipleChoice") {
        if (!question.options || question.options.length < 2) {
          newErrors[`options_${index}`] =
            "Multiple choice questions need at least 2 options";
        }
        question.options.forEach((option, optIndex) => {
          if (!option.trim()) {
            newErrors[`option_${index}_${optIndex}`] =
              "Option text cannot be empty";
          }
        });
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      if (settingsError) {
        dangerMessage(settingsError);
      } else {
        dangerMessage("Please fix the errors in the form");
      }
      return;
    }

    setIsSubmitting(true);

    try {
      let result;
      if (survey) {
        result = await updateSurvey(survey._id, formData, token);
      } else {
        result = await createSurvey(eventId, formData, token);
      }

      if (result?.status === "success") {
        successMessage(
          survey ? "Survey updated successfully" : "Survey created successfully"
        );
        if (onSuccess) {
          onSuccess();
        }
      } else {
        dangerMessage(result?.message || "Failed to save survey");
      }
    } catch (error) {
      dangerMessage("Error saving survey");
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Professional Toggle Switch Component with disabled support
  const ToggleSwitch = ({ id, name, checked, onChange, label, description, size = "md", disabled = false }) => {
    const isChecked = typeof checked === "boolean" ? checked : !!checked;
    
    return (
      <div className={`toggle-wrapper toggle-${size}`} style={{ opacity: disabled ? 0.5 : 1, cursor: disabled ? "not-allowed" : "pointer" }}>
        <label className="toggle-switch-label" htmlFor={id} style={{ pointerEvents: disabled ? "none" : "auto" }}>
          <input
            type="checkbox"
            id={id}
            name={name}
            checked={isChecked}
            onChange={(e) => onChange(name, e.target.checked)}
            className="toggle-input"
            disabled={disabled}
          />
          <span className="toggle-slider"></span>
        </label>
        <div className="toggle-content" style={{ pointerEvents: disabled ? "none" : "auto" }}>
          <span className="toggle-label">{label}</span>
          {description && (
            <span className="toggle-description">{description}</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="survey-form-wrapper">
      {/* Custom CSS */}
      <style>{`
        .survey-form-wrapper {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        }

        .survey-form-wrapper * {
          box-sizing: border-box;
        }

        /* Header Section */
        .survey-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 30px;
          flex-wrap: wrap;
          gap: 15px;
        }

        .survey-header h3 {
          margin: 0 0 5px 0;
          color: #1f2937;
          font-weight: 700;
          font-size: 1.5rem;
        }

        .survey-header p {
          margin: 0;
          color: #6b7280;
          font-size: 14px;
        }

        /* Cards */
        .survey-card {
          border: none;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          margin-bottom: 24px;
          overflow: hidden;
          background: white;
        }

        .survey-card-header {
          background: white;
          border-bottom: 2px solid #f3f4f6;
          padding: 20px 24px;
          font-weight: 600;
          font-size: 1.1rem;
          color: #1f2937;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .survey-card-header svg {
          color: #f97316;
        }

        .survey-card-body {
          padding: 24px;
        }

        /* Form Elements */
        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-label {
          font-weight: 500;
          color: #374151;
          margin-bottom: 8px;
          display: block;
          font-size: 14px;
        }

        .form-label .required {
          color: #ef4444;
        }

        .form-control-custom {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          transition: all 0.2s;
          background: white;
        }

        .form-control-custom:focus {
          outline: none;
          border-color: #f97316;
          box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1);
        }

        .form-control-custom::placeholder {
          color: #9ca3af;
        }

        .form-text-custom {
          font-size: 12px;
          color: #6b7280;
          margin-top: 4px;
        }

        .form-text-danger {
          font-size: 12px;
          color: #ef4444;
          margin-top: 4px;
        }

        /* Toggle Switch - Professional Design */
        .toggle-wrapper {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
        }

        .toggle-wrapper.toggle-sm {
          gap: 10px;
        }

        .toggle-switch-label {
          position: relative;
          display: inline-block;
          width: 52px;
          height: 28px;
          flex-shrink: 0;
          flex-grow: 0;
          cursor: pointer;
          margin: 0;
        }

        .toggle-wrapper.toggle-sm .toggle-switch-label {
          width: 48px;
          height: 26px;
        }

        .toggle-input {
          opacity: 0;
          width: 100%;
          height: 100%;
          position: absolute;
          z-index: 2;
          cursor: pointer;
          top: 0;
          left: 0;
          margin: 0;
          padding: 0;
        }

        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #e5e7eb;
          transition: all 0.3s ease;
          border-radius: 28px;
          border: 2px solid #d1d5db;
          pointer-events: none;
          z-index: 1;
        }

        .toggle-wrapper.toggle-sm .toggle-slider {
          border-radius: 26px;
        }

        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 20px;
          width: 20px;
          left: 2px;
          bottom: 2px;
          background-color: white;
          transition: all 0.3s ease;
          border-radius: 50%;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          z-index: 1;
        }

        .toggle-wrapper.toggle-sm .toggle-slider:before {
          height: 18px;
          width: 18px;
          left: 2px;
          bottom: 2px;
        }

        .toggle-input:checked + .toggle-slider {
          background-color: #f97316;
          border-color: #f97316;
        }

        .toggle-input:checked + .toggle-slider:before {
          transform: translateX(24px);
        }

        .toggle-wrapper.toggle-sm .toggle-input:checked + .toggle-slider:before {
          transform: translateX(22px);
        }

        .toggle-input:focus + .toggle-slider {
          box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.2);
        }

        .toggle-content {
          display: flex;
          flex-direction: column;
          gap: 2px;
          flex: 1;
          min-width: 0;
        }

        .toggle-wrapper.toggle-sm .toggle-content {
          flex-direction: row;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .toggle-label {
          font-weight: 600;
          color: #374151;
          font-size: 14px;
          white-space: nowrap;
        }

        .toggle-wrapper.toggle-sm .toggle-label {
          font-size: 13px;
        }

        .toggle-description {
          font-size: 13px;
          color: #6b7280;
          line-height: 1.4;
        }

        .toggle-wrapper.toggle-sm .toggle-description {
          font-size: 12px;
          display: none;
        }

        /* Setting Cards - Responsive */
        .setting-card {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          padding: 16px;
          height: 100%;
          transition: all 0.2s;
          display: flex;
          align-items: center;
        }

        .setting-card:hover {
          border-color: #f97316;
          background: #fff7ed;
        }

        .setting-card.disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .setting-card.disabled:hover {
          border-color: #e5e7eb;
          background: #f9fafb;
        }

        /* Question Cards */
        .question-card {
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          margin-bottom: 20px;
          background: white;
          transition: all 0.2s;
        }

        .question-card:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }

        .question-card-body {
          padding: 24px;
        }

        .question-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 1px solid #e5e7eb;
          flex-wrap: wrap;
          gap: 12px;
        }

        .question-badge {
          display: inline-flex;
          align-items: center;
          padding: 6px 12px;
          background: #eff6ff;
          color: #2563eb;
          border-radius: 20px;
          font-weight: 500;
          font-size: 13px;
        }

        .question-type {
          font-size: 13px;
          color: #6b7280;
        }

        .question-actions {
          display: flex;
          gap: 6px;
        }

        .btn-action {
          padding: 6px 10px;
          border: 1px solid #d1d5db;
          background: white;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .btn-action:hover:not(:disabled) {
          background: #f9fafb;
          border-color: #9ca3af;
        }

        .btn-action:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-action.delete {
          color: #ef4444;
        }

        .btn-action.delete:hover:not(:disabled) {
          background: #fef2f2;
          border-color: #ef4444;
        }

        /* Empty State */
        .empty-state {
          text-align: center;
          padding: 60px 20px;
          background: #f9fafb;
          border-radius: 10px;
        }

        .empty-state-icon {
          width: 64px;
          height: 64px;
          background: #e5e7eb;
          border-radius: 50%;
          margin: 0 auto 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #9ca3af;
        }

        .empty-state-text {
          color: #6b7280;
          margin-bottom: 16px;
        }

        /* Buttons */
        .btn-custom {
          border-radius: 8px;
          padding: 10px 20px;
          font-weight: 500;
          transition: all 0.2s;
          cursor: pointer;
          border: none;
          font-size: 14px;
        }

        .btn-primary-custom {
          background: #f97316;
          color: white;
        }

        .btn-primary-custom:hover {
          background: #ea580c;
        }

        .btn-secondary-custom {
          background: white;
          color: #374151;
          border: 1px solid #d1d5db;
        }

        .btn-secondary-custom:hover {
          background: #f9fafb;
        }

        .btn-outline-custom {
          background: transparent;
          color: #f97316;
          border: 1px solid #f97316;
        }

        .btn-outline-custom:hover {
          background: #fff7ed;
        }

        .btn-link-custom {
          background: transparent;
          color: #f97316;
          border: none;
          padding: 0;
          text-decoration: none;
        }

        .btn-link-custom:hover {
          text-decoration: underline;
        }

        /* Action Bar */
        .action-bar {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding-top: 20px;
          border-top: 1px solid #e5e7eb;
          flex-wrap: wrap;
        }

        /* Options List */
        .options-list {
          margin-bottom: 16px;
        }

        .option-item {
          display: flex;
          gap: 10px;
          margin-bottom: 10px;
          align-items: center;
          flex-wrap: wrap;
        }

        .option-number {
          color: #6b7280;
          font-weight: 500;
          min-width: 24px;
          font-size: 14px;
        }

        .option-input {
          flex: 1;
          min-width: 200px;
        }

        /* Responsive Design */
        @media (max-width: 992px) {
          .setting-card {
            margin-bottom: 12px;
          }
        }

        @media (max-width: 768px) {
          .survey-form-wrapper {
            padding: 15px;
          }

          .survey-header {
            flex-direction: column;
            gap: 15px;
          }

          .survey-header h3 {
            font-size: 1.25rem;
          }

          .survey-card-header {
            padding: 16px 20px;
            font-size: 1rem;
          }

          .survey-card-body {
            padding: 20px;
          }

          .setting-card {
            padding: 14px;
            margin-bottom: 12px;
          }

          .toggle-wrapper {
            flex-direction: row;
            align-items: center;
            width: 100%;
          }

          .toggle-switch-label {
            width: 48px !important;
            height: 26px !important;
            flex-shrink: 0 !important;
            flex-grow: 0 !important;
          }

          .toggle-slider:before {
            height: 18px;
            width: 18px;
          }

          .toggle-input:checked + .toggle-slider:before {
            transform: translateX(22px);
          }

          .toggle-content {
            flex-direction: row;
            align-items: center;
            gap: 8px;
            flex-wrap: wrap;
          }

          .toggle-label {
            font-size: 13px;
            white-space: nowrap;
          }

          .toggle-description {
            font-size: 12px;
            display: none;
          }

          .question-header {
            flex-direction: column;
            align-items: stretch;
          }

          .question-actions {
            justify-content: flex-start;
          }

          .option-item {
            flex-direction: row;
          }

          .option-input {
            min-width: 150px;
            flex: 1;
          }

          .action-bar {
            flex-direction: column-reverse;
          }

          .action-bar .btn-custom {
            width: 100%;
            text-align: center;
            padding: 12px 20px;
          }
        }

        @media (max-width: 480px) {
          .survey-form-wrapper {
            padding: 12px;
          }

          .survey-card-header {
            padding: 14px 16px;
          }

          .survey-card-body {
            padding: 16px;
          }

          .question-card-body {
            padding: 16px;
          }

          .form-control-custom {
            padding: 10px 12px;
            font-size: 14px;
          }

          .toggle-switch-label {
            width: 44px !important;
            height: 24px !important;
          }

          .toggle-slider:before {
            height: 16px;
            width: 16px;
          }

          .toggle-input:checked + .toggle-slider:before {
            transform: translateX(20px);
          }

          .toggle-label {
            font-size: 13px;
          }

          .question-badge {
            font-size: 12px;
            padding: 4px 10px;
          }

          .question-type {
            font-size: 12px;
          }

          .btn-action {
            padding: 5px 8px;
          }
        }

        /* Animation */
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .survey-card, .question-card {
          animation: slideIn 0.3s ease;
        }

        .row > [class*="col-"] {
          margin-bottom: 0;
        }

        .setting-card .toggle-wrapper {
          width: auto;
          max-width: 100%;
        }
      `}</style>

      {/* Header */}
      <div className="survey-header">
        <div>
          <h3>{survey ? "Edit Survey" : "Create Survey"}</h3>
          <p>{survey ? "Update your survey details and questions" : "Build a new survey for your event"}</p>
        </div>
        <button 
          className="btn-custom btn-secondary-custom" 
          onClick={onClose}
          disabled={isSubmitting}
        >
          Cancel
        </button>
      </div>

      <Form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <Card className="survey-card">
          <div className="survey-card-header">
            <FaListUl />
            <span>Basic Information</span>
          </div>
          <div className="survey-card-body">
            <div className="form-group">
              <Label className="form-label">
                Survey Title <span className="required">*</span>
              </Label>
              <Input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                invalid={!!errors.title}
                placeholder="Enter a clear, descriptive title"
                className="form-control-custom"
              />
              <div className="form-text-custom">
                {formData.title.length}/100 characters
              </div>
              {errors.title && (
                <div className="form-text-danger">{errors.title}</div>
              )}
            </div>

            <div className="form-group">
              <Label className="form-label">Description</Label>
              <Input
                type="textarea"
                name="description"
                rows="3"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Briefly describe the purpose of this survey"
                className="form-control-custom"
              />
              <div className="form-text-custom">
                {formData.description.length}/200 characters
              </div>
            </div>

            <ToggleSwitch
              id="isActive"
              name="isActive"
              checked={formData.isActive}
              onChange={handleToggleChange}
              label="Active Survey"
              description="Survey is currently accepting responses"
              size="sm"
            />
          </div>
        </Card>

        {/* Settings */}
        <Card className="survey-card">
          <div className="survey-card-header">
            <FaCog />
            <span>Response Settings</span>
          </div>
          <div className="survey-card-body">
            {/* ✅ Warning message for conflicting settings */}
            {settingsError && (
              <Alert color="warning" className="mb-3" style={{
                background: "#fef3c7",
                border: "1px solid #f59e0b",
                color: "#92400e",
                borderRadius: "8px",
                padding: "12px 16px",
                fontSize: "13px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
                <FaExclamationTriangle style={{ flexShrink: 0 }} />
                <span>{settingsError}</span>
              </Alert>
            )}
            
            <Row className="g-3">
              <Col xs="12" md="4">
                <div className={`setting-card ${formData.settings.requireEmail ? 'disabled' : ''}`}>
                  <ToggleSwitch
                    id="allowAnonymous"
                    name="settings.allowAnonymous"
                    checked={formData.settings.allowAnonymous}
                    onChange={handleToggleChange}
                    label="Allow Anonymous"
                    description="Respondents can submit without providing identity"
                    size="sm"
                    disabled={formData.settings.requireEmail} // Disable if requireEmail is enabled
                  />
                </div>
              </Col>

              <Col xs="12" md="4">
                <div className={`setting-card ${formData.settings.allowAnonymous ? 'disabled' : ''}`}>
                  <ToggleSwitch
                    id="requireEmail"
                    name="settings.requireEmail"
                    checked={formData.settings.requireEmail}
                    onChange={handleToggleChange}
                    label="Require Email"
                    description="Collect email addresses for follow-up"
                    size="sm"
                    disabled={formData.settings.allowAnonymous} // Disable if allowAnonymous is enabled
                  />
                </div>
              </Col>

              <Col xs="12" md="4">
                <div className="setting-card">
                  <ToggleSwitch
                    id="allowMultipleSubmissions"
                    name="settings.allowMultipleSubmissions"
                    checked={formData.settings.allowMultipleSubmissions}
                    onChange={handleToggleChange}
                    label="Multiple Submissions"
                    description="Allow respondents to submit more than once"
                    size="sm"
                  />
                </div>
              </Col>
            </Row>
            
            {/* Helper text */}
            <div style={{ marginTop: "12px", fontSize: "12px", color: "#6b7280" }}>
              <strong>Note:</strong> You cannot allow anonymous submissions and require email at the same time. 
              Choose the option that best fits your survey needs.
            </div>
          </div>
        </Card>

        {/* Questions */}
        <Card className="survey-card">
          <div className="survey-card-header">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", flexWrap: "wrap", gap: "15px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <FaListUl />
                <span>Questions</span>
              </div>
              <button
                type="button"
                className="btn-custom btn-primary-custom"
                onClick={addQuestion}
              >
                <FaPlus style={{ marginRight: "6px" }} />
                Add Question
              </button>
            </div>
          </div>
          <div className="survey-card-body">
            {formData.questions.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">
                  <FaListUl size={24} />
                </div>
                <p className="empty-state-text">No questions added yet</p>
                <button 
                  className="btn-custom btn-outline-custom" 
                  onClick={addQuestion}
                >
                  <FaPlus style={{ marginRight: "6px" }} />
                  Add Your First Question
                </button>
              </div>
            ) : (
              formData.questions.map((question, index) => (
                <Card key={index} className="question-card">
                  <div className="question-card-body">
                    {/* Question Header */}
                    <div className="question-header">
                      <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                        <span className="question-badge">Q{index + 1}</span>
                        <span className="question-type">
                          {question.type === 'text' && 'Text Answer'}
                          {question.type === 'rating' && 'Rating Scale'}
                          {question.type === 'multipleChoice' && 'Multiple Choice'}
                          {question.type === 'yesNo' && 'Yes/No'}
                        </span>
                      </div>
                      
                      <div className="question-actions">
                        <button
                          type="button"
                          className="btn-action"
                          onClick={() => moveQuestion(index, "up")}
                          disabled={index === 0}
                          title="Move up"
                        >
                          <FaArrowUp />
                        </button>
                        <button
                          type="button"
                          className="btn-action"
                          onClick={() => moveQuestion(index, "down")}
                          disabled={index === formData.questions.length - 1}
                          title="Move down"
                        >
                          <FaArrowDown />
                        </button>
                        <button
                          type="button"
                          className="btn-action delete"
                          onClick={() => removeQuestion(index)}
                          title="Delete question"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>

                    {/* Question Type */}
                    <div className="form-group">
                      <Label className="form-label" style={{ fontSize: "12px", textTransform: "uppercase", color: "#6b7280" }}>
                        Question Type
                      </Label>
                      <Input
                        type="select"
                        value={question.type}
                        onChange={(e) => updateQuestion(index, "type", e.target.value)}
                        className="form-control-custom"
                      >
                        <option value="text">Short Text</option>
                        <option value="rating">Rating Scale (1-10)</option>
                        <option value="multipleChoice">Multiple Choice</option>
                        <option value="yesNo">Yes / No</option>
                      </Input>
                    </div>

                    {/* Question Text */}
                    <div className="form-group">
                      <Label className="form-label">
                        Question Text <span className="required">*</span>
                      </Label>
                      <Input
                        type="text"
                        value={question.question}
                        onChange={(e) => updateQuestion(index, "question", e.target.value)}
                        invalid={!!errors[`question_${index}`]}
                        placeholder="What would you like to ask?"
                        className="form-control-custom"
                      />
                      {errors[`question_${index}`] && (
                        <div className="form-text-danger">{errors[`question_${index}`]}</div>
                      )}
                    </div>

                    {/* Required Toggle */}
                    <div style={{ marginBottom: "20px" }}>
                      <ToggleSwitch
                        id={`question_${index}_required`}
                        name={`question_${index}_required`}
                        checked={question.required}
                        onChange={handleToggleChange}
                        label="Required field"
                        size="sm"
                      />
                    </div>

                    {/* Rating Scale */}
                    {question.type === "rating" && (
                      <div className="form-group">
                        <Label className="form-label">Rating Scale Range</Label>
                        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                          <Input
                            type="number"
                            min="2"
                            max="10"
                            value={question.ratingScale || 5}
                            onChange={(e) => updateQuestion(index, "ratingScale", parseInt(e.target.value))}
                            invalid={!!errors[`rating_${index}`]}
                            className="form-control-custom"
                            style={{ width: "80px", textAlign: "center" }}
                          />
                          <span className="form-text-custom" style={{ flex: "1" }}>
                            Maximum rating value (2-10). Respondents will rate from 1 to this number.
                          </span>
                        </div>
                        {errors[`rating_${index}`] && (
                          <div className="form-text-danger">{errors[`rating_${index}`]}</div>
                        )}
                      </div>
                    )}

                    {/* Multiple Choice Options */}
                    {question.type === "multipleChoice" && (
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <Label className="form-label">
                          Answer Options <span className="required">*</span>
                          <span style={{ fontWeight: "400", color: "#6b7280", marginLeft: "6px" }}>(minimum 2)</span>
                        </Label>
                        
                        <div className="options-list">
                          {question.options?.map((option, optIndex) => (
                            <div key={optIndex} className="option-item">
                              <span className="option-number">{optIndex + 1}.</span>
                              <Input
                                type="text"
                                value={option}
                                onChange={(e) => updateOption(index, optIndex, e.target.value)}
                                placeholder={`Option ${optIndex + 1}`}
                                invalid={!!errors[`option_${index}_${optIndex}`]}
                                className="form-control-custom option-input"
                              />
                              <button
                                type="button"
                                className="btn-action delete"
                                onClick={() => removeOption(index, optIndex)}
                                title="Remove option"
                              >
                                <FaTrash size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                        
                        {errors[`options_${index}`] && (
                          <div className="form-text-danger" style={{ marginBottom: "12px" }}>
                            {errors[`options_${index}`]}
                          </div>
                        )}
                        
                        <button
                          type="button"
                          className="btn-custom btn-link-custom"
                          onClick={() => addOption(index)}
                        >
                          <FaPlus style={{ marginRight: "4px" }} />
                          <span style={{ fontWeight: "500" }}>Add another option</span>
                        </button>
                      </div>
                    )}
                  </div>
                </Card>
              ))
            )}
          </div>
        </Card>

        {/* Action Buttons */}
        <div className="action-bar">
          <button 
            className="btn-custom btn-secondary-custom" 
            onClick={onClose} 
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button 
            className="btn-custom btn-primary-custom" 
            type="submit" 
            disabled={isSubmitting}
            style={{ padding: "10px 32px" }}
          >
            {isSubmitting ? (
              <>
                <Spinner size="sm" style={{ marginRight: "8px" }} />
                Saving...
              </>
            ) : survey ? (
              "Update Survey"
            ) : (
              "Create Survey"
            )}
          </button>
        </div>
      </Form>
    </div>
  );
};

export default SurveyForm;